import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';


import { catchError, Subscription, tap } from 'rxjs';
import { AuthService } from 'src/lib/auth/auth.service';
import { BrandService } from 'src/lib/services/brand.service';
import { HomeScreenService } from 'src/lib/services/home-screen.service';
import { DialogService } from '../../../services/dialog.service';
import { SharedModule } from '../../../shared/shared.module';
import { DialogAbstract } from '../../dialog.abstract';
import { InputWrapperModule } from '../../input-wrapper/input-wrapper.module';

/**
 * 로그인 다이얼로그 컴포넌트
 * 사용자 인증을 위한 로그인 폼을 제공합니다.
 */
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    InputWrapperModule
],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent
  extends DialogAbstract
  implements OnInit, OnDestroy
{
  /** 비밀번호 입력 폼 요소 참조 */
  @ViewChild('pwForm') pwForm?: ElementRef;

  /** 다이얼로그 구독 객체 */
  dialogSubscription?: Subscription;

  /** 인증 관련 구독 객체 */
  authSubscription?: Subscription;

  /** 로그인 폼 그룹 정의 */
  loginForm = this.formBuilder.group({
    id: [null, Validators.required],
    pw: [null, Validators.required],
    // shouldSaveId: [null],
  });

  /** 비밀번호 표시 여부 플래그 */
  isPasswordVisible = false;

  /** 홈페이지 로고 이미지 URL */
  homepageLogoImageUrl?: string;

  /** 로그인 페이지 안내문 */
  loginPageContent?: string;

  /** 로그인 처리 중 여부 */
  isLoggingIn = false;

  constructor(
    protected override elementRef: ElementRef<HTMLElement>,
    private router: Router,
    private formBuilder: FormBuilder,
    private dialogService: DialogService,
    private translateService: TranslateService,
    private authService: AuthService,
    private brandService: BrandService,
    private homeScreenService: HomeScreenService,
  ) {
    super(elementRef);
  }

  /**
   * 컴포넌트 초기화
   * - 홈페이지 로고 이미지 URL 설정
   * - 로그인 페이지 안내문 설정
   */
  override ngOnInit(): void {
    super.ngOnInit();

    this.homepageLogoImageUrl =
      this.homeScreenService.homeScreen.homepageLogoImageFileData?.path ||
      this.homeScreenService.homeScreen.homepageLogoImageUrl ||
      this.brandService.brand.logoImgFileData?.path ||
      this.brandService.brand.logoImgUrl!;

    this.loginPageContent = this.homeScreenService.homeScreen.loginPageContent;
  }

  /**
   * 로그인 버튼 클릭 핸들러
   * - 폼 유효성 검사
   * - 로그인 API 호출
   * - 성공/실패 처리
   */
  onLoginClick(): void {
    // 이미 로그인 처리 중이면 중복 실행 방지
    if (this.isLoggingIn) {
      return;
    }

    if (this.loginForm.invalid) {
      this.dialogSubscription = this.dialogService
        .alert(this.translateService.instant('ALERT.Required_ID_PW'))
        .subscribe();
      return;
    }

    const { id, pw } = this.loginForm.value;

    // 로그인 처리 시작
    this.isLoggingIn = true;

    this.authSubscription = this.authService
      .login(id!, pw!, 'USERINFO', this.brandService.brand.id)
      .pipe(
        tap(() => {
          this.dialogRef.close(true);
        }),
        catchError(() => {
          return this.dialogService.alert(
            this.translateService.instant('ALERT.Login_Error'),
          );
        }),
      )
      .subscribe({
        next: () => {
          // 로그인 처리 완료
          this.isLoggingIn = false;
        },
        error: () => {
          // 에러 발생 시에도 로그인 처리 상태 초기화
          this.isLoggingIn = false;
        },
      });
  }

  /**
   * 계정 정보 찾기 버튼 클릭 핸들러
   * TODO: 실제 구현 필요
   */
  onFindInfoClick(): void {
    // FIXME: 정보 찾기 구현
    this.dialogService.alert('ALERT.Req_Accout_Travel_Agency').subscribe();
  }

  /**
   * 회원가입 버튼 클릭 핸들러
   * - 회원가입 페이지로 이동
   * - 회원가입 비활성화 시 알림 표시
   */
  onJoinClick(): void {
    if (this.homeScreenService.homeScreen.userSignUp) {
      this.router.navigate(['requests', 'user-info-join']).then(() => {
        this.dialogRef.close();
      });
    } else {
      this.dialogService.alert('ALERT.Req_Register_Travel_Agency').subscribe();
    }
  }

  /**
   * 입력 필드 키다운 이벤트 핸들러
   * - 공백 입력 방지
   */
  onInputKeydown(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      // 공백 입력 무시
      event.preventDefault();
    }
  }

  /**
   * 입력 필드 입력 이벤트 핸들러
   * - 붙여넣기 시 공백 제거
   */
  onInput(event: Event): void {
    const inputEvent = event as InputEvent;
    if (inputEvent.inputType.includes('insertFromPaste')) {
      // 붙어넣기 이벤트 발생
      const element = inputEvent.target as HTMLInputElement;
      // 공백 제거
      element.value = element.value.replace(/\s+/g, '');
    }
  }

  /**
   * 컴포넌트 소멸 시 구독 해제
   */
  override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.authSubscription?.unsubscribe();
    this.dialogSubscription?.unsubscribe();
  }
}
